import React from 'react'
import cn from 'classnames'
import { TFunction } from 'next-i18next/pages'
import { Accordion } from '@corratech/pylot-ui'
import { RichContent } from '@corratech/pylot-rich-content'
import type {
    CmsBlock,
    ComplexTextValue,
    ConfigurableProduct,
    Maybe
} from '@pylot/pylotschema'
import s from '@pagestyles/PDP.module.scss'
import { productCustomAttributes } from '@pylot/constants/productCustomAttributes'

export type CustomAttributeEntry = {
    key: string
    label: string
    value: string
}

export function getCustomAttributeEntries(
    product: ConfigurableProduct
): CustomAttributeEntry[] {
    return Object.keys(productCustomAttributes)
        .map((key) => {
            const value = (product as any)?.[key]
            if (value === null || value === undefined || value === '')
                return null
            return {
                key,
                label: (productCustomAttributes as any)[key] as string,
                value: value as string
            }
        })
        .filter(Boolean) as CustomAttributeEntry[]
}

const DescriptionTab = React.memo(function DescriptionTab({
    shortDescriptionHtml,
    customAttributes
}: {
    shortDescriptionHtml?: string
    customAttributes: CustomAttributeEntry[]
}) {
    return (
        <>
            <div className={s['short-description']}>
                <RichContent html={shortDescriptionHtml} />
            </div>

            {customAttributes.map(({ key, label, value }) => (
                <div key={key} className="mb-5 md:mb-6">
                    <h6 className="mb-2 font-tertiary font-medium text-sm md:text-base leading-extra-4.4 md:leading-5">
                        {label}
                    </h6>
                    <div className="font-primary font-normal text-sm md:text-base leading-5 md:leading-extra-1.6">
                        <RichContent html={value} />
                    </div>
                </div>
            ))}
        </>
    )
})

const CmsTab = React.memo(function CmsTab({ html }: { html?: string | null }) {
    return (
        <div className="flex flex-col gap-y-4">
            <RichContent html={html ?? undefined} />
        </div>
    )
})

export function buildPdpAccordionContents(args: {
    t: TFunction
    short_description: Maybe<ComplexTextValue> | undefined
    firstAccordionBlockContent: Maybe<Maybe<CmsBlock | null>[]> | undefined
    secondAccordionBlockContent: Maybe<Maybe<CmsBlock | null>[]> | undefined
    customAttributes: CustomAttributeEntry[]
}): React.ComponentProps<typeof Accordion>['contents'] {
    const {
        t,
        short_description,
        firstAccordionBlockContent,
        secondAccordionBlockContent,
        customAttributes
    } = args

    return [
        {
            id: 'short-description',
            Content: function AccordionContent() {
                return (
                    <DescriptionTab
                        shortDescriptionHtml={
                            short_description?.html ?? undefined
                        }
                        customAttributes={customAttributes}
                    />
                )
            },
            buttonClass: ({ isOpen }: { isOpen: boolean }) =>
                cn(
                    {
                        'mb-6 md:mb-9': isOpen
                    },
                    'py-5'
                ),
            Title: function AccordionTitle() {
                return (
                    <h5 className="uppercase font-bold text-base md:text-xl leading-5 md:leading-6">
                        {t('pdp||Product description')}
                    </h5>
                )
            },
            initialState: true
        },
        {
            id: 'payment-methods',
            Title: function AccordionTitle() {
                return (
                    <h5 className={cn('uppercase text-base md:text-xl mr-2')}>
                        {t('pdp||Payment methods')}
                    </h5>
                )
            },
            Content: function AccordionContent() {
                return (
                    <CmsTab
                        html={firstAccordionBlockContent?.[0]?.content ?? null}
                    />
                )
            },
            buttonClass: ({ isOpen }: { isOpen: boolean }) =>
                cn(
                    {
                        'mb-6 md:mb-9': isOpen
                    },
                    'py-5'
                ),
            buttonStyles: () => ({
                justifyContent: 'space-between'
            }),
            itemClassName: 'border-b border-color-s-4'
        },
        {
            id: 'returns-exchanges',
            Title: function AccordionTitle() {
                return (
                    <h5 className={cn('uppercase text-base md:text-xl mr-2')}>
                        {t('pdp||Returns and exchanges')}
                    </h5>
                )
            },
            Content: function AccordionContent() {
                return (
                    <CmsTab
                        html={secondAccordionBlockContent?.[0]?.content ?? null}
                    />
                )
            },
            buttonClass: ({ isOpen }: { isOpen: boolean }) =>
                cn(
                    {
                        'mb-6 md:mb-9': isOpen
                    },
                    'py-5'
                ),
            buttonStyles: () => ({
                justifyContent: 'space-between'
            }),
            itemClassName: 'border-b border-color-s-4'
        }
    ]
}
